import { useState, ChangeEvent, DragEvent, useEffect } from 'react';
import {
  checkMaxFiles,
  checkMaxUploadSize,
  checkFileType,
} from './FEValidation';
import { FileText, Image, ShieldAlert, UploadIcon, X } from 'lucide-react';

interface DropzoneProps {
  inputData: any;
  onChangeHandler: any;
  errorMessageProp: string | null;
  isRequired?: boolean;
}

export const Dropzone: React.FC<DropzoneProps> = ({
  errorMessageProp,
  inputData,
  onChangeHandler,
  isRequired,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedFilesSize, setUploadedFilesSize] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(
    errorMessageProp
  );

  useEffect(() => {
    setErrorMessage(errorMessageProp);
  }, [errorMessageProp]);

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();

    const files = e.dataTransfer.files;

    const sizeValidation = checkMaxUploadSize(files, uploadedFilesSize);
    const fileValidation = checkMaxFiles(files, uploadedFiles);
    const typeValidation = checkFileType(files);
    let error: string | null = null;

    if (
      !fileValidation.isValid ||
      !sizeValidation.isValid ||
      !typeValidation.isValid
    ) {
      const newFiles: File[] = [];
      handleFileInput(newFiles);

      error =
        typeValidation.errorMessage ||
        fileValidation.errorMessage ||
        sizeValidation.errorMessage ||
        null;
      setErrorMessage(error);
      return;
    }

    setErrorMessage(null);
    handleFiles(files);
  };

  const onDragOver = (e: DragEvent) => {
    e.preventDefault();
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (files) {
      const sizeValidation = checkMaxUploadSize(files, uploadedFilesSize);
      const fileValidation = checkMaxFiles(files, uploadedFiles);
      const typeValidation = checkFileType(files);
      let error: string | null = null;

      if (
        !fileValidation.isValid ||
        !sizeValidation.isValid ||
        !typeValidation.isValid
      ) {
        const newFiles: File[] = [];
        handleFileInput(newFiles);

        error =
          typeValidation.errorMessage ||
          fileValidation.errorMessage ||
          sizeValidation.errorMessage ||
          null;
        setErrorMessage(error);
        return;
      }

      setErrorMessage(null);
      handleFiles(files);
    }
  };

  const handleFiles = (files: FileList) => {
    const newFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      newFiles.push(files[i]);

      const filesize = files[i].size / (1024 * 1024);

      setUploadedFilesSize(prevSize => prevSize + filesize);
    }

    handleFileInput(newFiles);
  };

  const handleFileInput = (newFiles: File[]) => {
    setUploadedFiles(prevUploadedFiles => {
      const allFiles = [...prevUploadedFiles, ...newFiles];

      const dataTransfer = new DataTransfer();
      allFiles.forEach(file => dataTransfer.items.add(file));

      const input = document.getElementById(
        'dropzone-file'
      ) as HTMLInputElement;
      input.files = dataTransfer.files;

      onChangeHandler({
        ...inputData,
        files: allFiles,
      });
      return allFiles;
    });
  };

  const handleRemoveFile = (indexToRemove: number) => {
    setUploadedFiles(prevFiles => {
      const fileToRemove = prevFiles[indexToRemove];
      const fileSizeMB = fileToRemove.size / (1024 * 1024);

      setUploadedFilesSize(prevSize => prevSize - fileSizeMB);

      const remainingFiles = prevFiles.filter((_, i) => i !== indexToRemove);

      const dataTransfer = new DataTransfer();
      remainingFiles.forEach(file => dataTransfer.items.add(file));

      const fileInput = document.getElementById(
        'dropzone-file'
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.files = dataTransfer.files;
      }

      return remainingFiles;
    });
  };

  return (
    <>
      <label
        htmlFor="dropzone-file"
        className="flex flex-col text-center items-center justify-center w-full min-h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
      >
        <div
          className="flex flex-col items-center justify-center pt-5 pb-6 w-full min-h-64"
          onDrop={handleDrop}
          onDragOver={onDragOver}
        >
          <UploadIcon className="object-cover h-8 w-auto text-inglelewer-pink" />
          <p className="text-lg text-gray-500 font-bold">
            Click to upload a file or Drag and Drop
          </p>
          <p className="text-sm text-gray-500">
            A <span className="font-bold">maximum of 5 files</span> can be
            uploaded.
          </p>
          <p className="text-sm text-gray-500">
            Total size of all uploaded files should not be any larger than{' '}
            <span className="font-bold">10 MB</span>
          </p>
          <p className="text-sm text-gray-500">
            (Only PNG, JPG, JPEG or PDF file types are accepted)
          </p>
          <input
            id="dropzone-file"
            type="file"
            name="claimFiles"
            accept="image/png, image/jpeg, application/pdf"
            className="opacity-0"
            onChange={handleFileSelect}
            multiple
            {...(isRequired ? { required: true } : {})}
          />
          <button
            type="button"
            className="mt-4 px-4 py-2 rounded-md text-white bg-inglelewer-purple hover:bg-inglelewer-pink focus:ring-4 focus:ring-blue-300 cursor-pointer"
            onClick={() => {
              const fileInput = document.getElementById(
                'dropzone-file'
              ) as HTMLInputElement;
              if (fileInput) {
                fileInput.click();
              }
            }}
          >
            Select files
          </button>
        </div>
      </label>
      <ul className="list-none">
        {uploadedFiles.map((file, index) => (
          <li
            key={index}
            className="flex flex-row items-center text-sm md:text-base border border-[#707070] py-4 rounded-xl mt-2"
          >
            {file.type.startsWith('image/') ? (
              <div className="ml-4 mr-4">
                <Image
                  className="h-10 w-auto text-inglelewer-pink"
                  strokeWidth={0.75}
                />
              </div>
            ) : (
              <div className="ml-4 mr-4">
                <FileText
                  className="h-10 w-auto text-inglelewer-pink"
                  strokeWidth={0.75}
                />
              </div>
            )}
            <div className="flex flex-col text-[#66676E]">
              <p className="font-bold">{file.name}</p>
              <p>{(file.size / (1024 * 1024)).toFixed(3)} MB</p>
            </div>
            <button
              type="button"
              className="text-sm md:text-base ml-auto pr-4 h-6 cursor-pointer hover:opacity-50"
              onClick={() => handleRemoveFile(index)}
            >
              <X />
            </button>
          </li>
        ))}
        {errorMessage && (
          <li className="flex flex-row items-center text-sm md:text-base border-2 border-inglelewer-pink py-4 rounded-xl mt-2">
            <div className="ml-4 mr-4">
              <ShieldAlert className="h-10 w-auto text-inglelewer-pink" />
            </div>
            <div className="flex flex-col text-[#66676E]">
              <p className="font-bold">{errorMessage}</p>
            </div>
          </li>
        )}
      </ul>
    </>
  );
};
